import { EventAction, EventCategory, EventLabel } from "../BNGoogleTagManager";
import { DataLayerEvent } from "./DataLayerEvent";

export type ApplicationEventCategory =
  | EventCategory.CreditCardApplication
  | EventCategory.CreditCardLimitIncreaseApplication
  | EventCategory.LoanApplication
  | EventCategory.GreenLoanApplication
  | EventCategory.CreditPurchaseApplication
  | EventCategory.TopUpApplication
  | EventCategory.LoanExtensionApplication
  | EventCategory.SavingsAccountApplication
  | EventCategory.SavingsAccountPlusApplication
  | EventCategory.SavingsAccountDelayedWithdrawalApplication
  | EventCategory.SavingsAccountFixedTerm12Application
  | EventCategory.SavingsAccountFixedTerm24Application
  | EventCategory.CreditLineApplication
  | EventCategory.CreditLineLimitIncreaseApplication
  | EventCategory.EquityReleaseApplication
  | EventCategory.NotSet;

interface ApplicationFieldOptions {
  category: ApplicationEventCategory;
  formFieldName: string;
  formFieldResult?: ApplicationFieldResult;
  formFieldValue?: string;
  errorMsg?: string;
}
export class ApplicationFieldFilledDataLayerEvent extends DataLayerEvent {
  constructor({ category, ...options }: ApplicationFieldOptions) {
    super({
      eventName: getEventName(category, "input_field_filled"),
      data: {
        input_field: options.formFieldName,
        input_result: options.formFieldResult,
        input_value: options.formFieldValue,
        error_msg: options.errorMsg,
      },
    });
  }
}

interface ApplicationInputFieldErrorOptions {
  category: ApplicationEventCategory;
  formFieldName: string;
  errorMsg: string;
}

export class ApplicationInputFieldErrorDataLayerEvent extends DataLayerEvent {
  constructor({ category, formFieldName, errorMsg }: ApplicationInputFieldErrorOptions) {
    super({
      eventName: getEventName(category, "input_field_error"),
      data: {
        input_field: formFieldName,
        error_msg: errorMsg,
      },
    });
  }
}

export class ApplicationFieldPrefilledDataLayerEvent extends DataLayerEvent {
  constructor({ category, ...options }: ApplicationFieldOptions) {
    super({
      eventName: getEventName(category, "input_field_prefilled"),
      data: {
        input_field: options.formFieldName,
        input_result: options.formFieldResult,
      },
    });
  }
}

interface ApplicationFormStepChangeOptions {
  category: ApplicationEventCategory;
  step: string;
}
export class ApplicationFormNextStepDataLayerEvent extends DataLayerEvent {
  constructor({ category, step }: ApplicationFormStepChangeOptions) {
    super({
      eventName: getEventName(category, "form_click_next"),
      data: {
        app_step: step,
      },
    });
  }
}
interface ApplicationAuthenticationOptions {
  category: ApplicationEventCategory;
  status?: number;
  coApp?: boolean;
}

export class ApplicationFormAuthenticationDataLayerEvent extends DataLayerEvent {
  constructor({ category, status, coApp }: ApplicationAuthenticationOptions) {
    super({
      eventName: getEventName(category, `auth${coApp ? "-coApp" : ""}`),
      data: {
        result: getAuthenticationStatus(status),
      },
    });
  }
}

interface ApplicationESkatConsentFirstOptions {
  category: ApplicationEventCategory;
  resultString: string;
}

export class ApplicationFormESkatConsentFirstDataLayerEvent extends DataLayerEvent {
  constructor({ category, resultString }: ApplicationESkatConsentFirstOptions) {
    super({
      eventName: getEventName(category, EventAction.ESkatConsent),
      data: {
        result: resultString,
      },
    });
  }
}

export class ApplicationFormPreviousStepDataLayerEvent extends DataLayerEvent {
  constructor({ category, step }: ApplicationFormStepChangeOptions) {
    super({
      eventName: getEventName(category, "form_click_previous"),
      data: {
        app_step: step,
      },
    });
  }
}

interface ApplicationReceivedOptions {
  category: ApplicationEventCategory;
  grantedAmount?: number;
  appliedAmount?: number;
  isRefinance?: boolean;
  xfrl?: boolean;
  accommodationType?: string;
}
const getAmountValue = (category, appliedAmount, grantedAmount): number | undefined => {
  if (category === EventCategory.CreditCardApplication || category === EventCategory.CreditLineApplication) {
    return appliedAmount || grantedAmount;
  }
  if (appliedAmount != undefined && grantedAmount != undefined && appliedAmount < grantedAmount) {
    return appliedAmount;
  }

  if (appliedAmount == undefined) {
    return grantedAmount;
  }
  if (grantedAmount == undefined) {
    return appliedAmount;
  }

  return grantedAmount;
};

export class ApplicationReceivedDataLayerEvent extends DataLayerEvent {
  constructor({
    category,
    grantedAmount,
    appliedAmount,
    isRefinance,
    xfrl,
    accommodationType,
  }: ApplicationReceivedOptions) {
    super({
      eventName: getEventName(category, EventAction.Received),
      data: {
        value: getAmountValue(category, appliedAmount, grantedAmount),
        accommodationType: accommodationType,
        xfrl: DataLayerEvent.booleanToString(xfrl),
        is_refinance: DataLayerEvent.booleanToString(isRefinance),
      },
      objectProperties: {
        xfrl: DataLayerEvent.booleanToString(xfrl),
      },
    });
  }
}

export class SliderLoadedDataLayerEvent extends DataLayerEvent {
  constructor({ prefix, category, baseDefaultAmount, urlOverrideAmount, initialAmount }: SliderLoadedOptions) {
    super({
      eventName: getEventName(category, "slider_loaded"),
      data: {
        [`${prefix}_base_default_amount`]: baseDefaultAmount,
        [`${prefix}_url_override_amount`]: urlOverrideAmount,
        [`${prefix}_initial_amount`]: initialAmount,
      },
    });
  }
}
interface SliderLoadedOptions {
  prefix: string;
  category: ApplicationEventCategory;
  baseDefaultAmount: number;
  urlOverrideAmount?: number;
  initialAmount: number;
}

export class AmountSelectedDataLayerEvent extends DataLayerEvent {
  constructor({ prefix, category, initialAmount, selectedAmount }: AmountSelectedOptions) {
    super({
      eventName: getEventName(category, "amount_selected"),
      data: {
        [`${prefix}_initial_amount`]: initialAmount,
        [`${prefix}_selected_amount`]: selectedAmount,
        [`${prefix}_change_from_initial_amount`]: selectedAmount - initialAmount,
      },
    });
  }
}
interface AmountSelectedOptions {
  prefix: string;
  category: ApplicationEventCategory;
  initialAmount: number;
  selectedAmount: number;
}

interface DownPaymentDurationChangedOptions {
  applicationId?: number;
  prefix?: string;
}
export class DownPaymentDurationChangedDataLayerEvent extends DataLayerEvent {
  constructor({ applicationId, prefix = "ul" }: DownPaymentDurationChangedOptions) {
    super({
      eventName: `my_${prefix}_application`,
      data: {
        application_id: applicationId,
        checktype_name: EventAction.RepaymentPeriod,
        checktype_value: EventLabel.Edit,
      },
    });
  }
}

interface CreditLimitChangedOptions {
  applicationId?: number;
  prefix?: string;
}
export class CreditLimitChangedDataLayerEvent extends DataLayerEvent {
  constructor({ applicationId, prefix = "ul" }: CreditLimitChangedOptions) {
    super({
      eventName: `my_${prefix}_application`,
      data: {
        application_id: applicationId,
        checktype_name: EventAction.ModifyCreditLimit,
        checktype_value: EventLabel.Edit,
      },
    });
  }
}

export type ApplicationSubmittedEventAction =
  | "None"
  | "Approved"
  | "Rejected"
  | "Pending"
  | "DoItOnMyPage"
  | "PossibleFraud"
  | "RejectedWithReason"
  | "Error"
  | "InternalError";

interface ApplicationSubmittedOptions {
  category: ApplicationEventCategory;
  action: ApplicationSubmittedEventAction;
  applicationId?: number;
  userId?: number;
  grantedAmount?: number;
  appliedAmount?: number;
  isRefinance?: boolean;
  ecOrderId?: string;
  ecIdentifier1?: string;
  xfrl?: boolean;
  accommodationType?: string;
}
export class ApplicationSubmittedDataLayerEvent extends DataLayerEvent {
  constructor({
    category,
    action,
    applicationId,
    userId,
    grantedAmount,
    appliedAmount,
    isRefinance,
    ecOrderId,
    ecIdentifier1,
    xfrl,
    accommodationType,
  }: ApplicationSubmittedOptions) {
    super({
      eventName: getEventName(category, action),
      data: {
        value: getAmountValue(category, appliedAmount, grantedAmount),
        application_id: (applicationId || undefined)?.toString(), // 0 and empty string should not be sent
        user_id: (userId || undefined)?.toString(),
        ...(isCreditLimitCategory(category) && { credit_limit: grantedAmount }),
        ...(isLoanCategory(category) && {
          loan_amount: getAmountValue(category, appliedAmount, grantedAmount),
        }),
        is_refinance: DataLayerEvent.booleanToString(isRefinance),
        xfrl: DataLayerEvent.booleanToString(xfrl),
        accommodationType,
      },
      objectProperties: {
        ECOrderId: ecOrderId,
        ECIdentifier1: ecIdentifier1,
        xfrl: DataLayerEvent.booleanToString(xfrl),
      },
    });
  }
}

export enum ApplicationFieldResult {
  ValidationOk = "validation_ok",
  ValidationError = "validation_error",
  Warning = "warning",
  Error = "error",
  Ok = "ok",
}

export enum AuthenticationStatus {
  Authenticated = "Authenticated",
  SSNMismatch = "SSNMismatch",
  UnknownError = "UnknownError",
}

const getAuthenticationStatus = (i?: number): string => {
  switch (i) {
    case 1:
      return AuthenticationStatus.Authenticated;
    case 2:
      return AuthenticationStatus.SSNMismatch;
    case 4:
      return AuthenticationStatus.UnknownError;
    default:
      return "NotSet";
  }
};

const getEventName = (category: EventCategory, action: string): string =>
  `${getEventNameCategory(category)}_${action.toLowerCase()}`;

const getEventNameCategory = (c: EventCategory): string => {
  switch (c) {
    case EventCategory.CreditCardApplication:
      return "cc_application";
    case EventCategory.CreditCardLimitIncreaseApplication:
      return "ccli_application";
    case EventCategory.LoanApplication:
      return "ul_application";
    case EventCategory.GreenLoanApplication:
      return "gl_application";
    case EventCategory.CreditPurchaseApplication:
      return "cp_application";
    case EventCategory.TopUpApplication:
      return "tu_application";
    case EventCategory.LoanExtensionApplication:
      return "le_application";
    case EventCategory.SavingsAccountApplication:
      return "sa_application";
    case EventCategory.SavingsAccountPlusApplication:
      return "sw_application";
    case EventCategory.SavingsAccountDelayedWithdrawalApplication:
      return "sd_application";
    case EventCategory.SavingsAccountFixedTerm12Application:
      return "sf12_application";
    case EventCategory.SavingsAccountFixedTerm24Application:
      return "sf24_application";
    case EventCategory.CreditLineApplication:
      return "cl_application";
    case EventCategory.CreditLineLimitIncreaseApplication:
      return "clli_application";
    case EventCategory.EquityReleaseApplication:
      return "er_application";
  }

  // eslint-disable-next-line no-console
  console.warn("No case for category", c);
  return "unknown";
};

const isLoanCategory = (category: EventCategory): boolean => {
  return (
    category === EventCategory.LoanApplication ||
    category === EventCategory.GreenLoanApplication ||
    category === EventCategory.CreditPurchaseApplication ||
    category === EventCategory.TopUpApplication
  );
};

const isCreditLimitCategory = (category: EventCategory): boolean => {
  return (
    category === EventCategory.CreditLineLimitIncreaseApplication ||
    category === EventCategory.CreditLineApplication ||
    isCreditCardCategory(category)
  );
};

const isCreditCardCategory = (category: EventCategory): boolean => {
  return (
    category === EventCategory.CreditCardApplication || category === EventCategory.CreditCardLimitIncreaseApplication
  );
};
